/**
 * 剖面工具插件 —— 实现 ActivatableToolPlugin 接口。
 * 激活后通过点击拾取场景点，构建剖面路径并计算默认宽度。
 * 不涉及 DOM/渲染，纯数据逻辑由 ProfileData 提供。
 */
import * as THREE from 'three'
import { WEBGL2_ONLY_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import {
  getActiveViewCamera,
  getOptionalViewRegistry,
  isActiveViewFrame,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import type { InputConsumer, NormalizedPointerEvent } from '@/shared/types/core'
import type { ActivatableToolPlugin, ActivationToken } from '@/shared/types/plugin'
import type { InputRouter, PickingService, ViewRegistry } from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import type { ProfilePoint, ProfileResult, ProfileSectionPoint } from './ProfileData'
import { buildProfileResult, computeDefaultWidth } from './ProfileData'

/**
 * 剖面工具插件。
 * 激活后通过左键点击拾取路径点，双击完成剖面，右键取消当前绘制。
 */
export class ProfilePlugin implements ActivatableToolPlugin {
  /** 插件名称 */
  readonly name = 'profile'
  readonly graphicsRequirements = WEBGL2_ONLY_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 50
  /** 所属互斥组 */
  readonly group = 'default'
  /** 依赖的服务名列表 */
  readonly dependencies = ['PickingService', 'InputRouter'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['ProfileService'] as const

  /** 当前是否激活 */
  private _active = false

  /** 由 PluginManager 注入的合法 token */
  private activationToken: ActivationToken | null = null

  /** 引擎上下文 */
  private context!: EngineContext

  /** 输入路由服务 */
  private inputRouter!: InputRouter

  /** 拾取服务 */
  private pickingService!: PickingService

  /** 已完成的剖面结果列表 */
  private results: ProfileResult[] = []

  /** 当前正在绘制的剖面点 */
  private currentPoints: ProfilePoint[] = []

  /** 缓存当前帧相机引用，供输入回调使用 */
  private currentCamera: THREE.Camera | null = null
  private viewRegistry: ViewRegistry | null = null
  private readonly handleViewActivated = (event: {
    viewId: string
    previousViewId: string | null
  }) => {
    this.currentCamera = this.resolveActiveViewCamera()
    if (event.previousViewId && event.previousViewId !== event.viewId) {
      this.cancelCurrentProfile()
    }
  }

  /** 输入消费者实例 */
  private readonly consumer: InputConsumer = {
    name: 'profile',
    priority: 100,

    onPointerDown: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerDown(event)
    },

    onPointerMove: (_event: NormalizedPointerEvent): boolean => {
      return false
    },

    onPointerUp: (_event: NormalizedPointerEvent): boolean => {
      return false
    },

    onDoubleClick: (_event: NormalizedPointerEvent): boolean => {
      this.finishCurrentProfile()
      return true
    },
  }

  /** 当前是否激活 */
  get isActive(): boolean {
    return this._active
  }

  // ==================== 生命周期 ====================

  /**
   * 由 PluginManager 注入合法 token。
   *
   * @param token - 激活令牌
   */
  setActivationToken(token: ActivationToken): void {
    this.activationToken = token
  }

  /**
   * 初始化：存储引擎上下文，注册服务。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    context.registerService('ProfileService', this)
  }

  /** 启动：从上下文获取所需服务 */
  onStart(): void {
    this.inputRouter = this.context.getService('InputRouter')
    this.pickingService = this.context.getService('PickingService')
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.currentCamera = this.resolveActiveViewCamera()
    if (this.viewRegistry) {
      this.context.events.on('view.activated', this.handleViewActivated)
    }
  }

  /**
   * 渲染视图前缓存当前相机引用。
   *
   * @param frame - 视图帧上下文
   */
  onRenderView(frame: ViewFrameContext): void {
    if (!isActiveViewFrame(this.viewRegistry, frame)) return

    this.currentCamera = this.resolveActiveViewCamera() ?? frame.camera
  }

  /** 销毁：确保已反激活 */
  onDestroy(): void {
    if (this.viewRegistry) {
      this.context.events.off('view.activated', this.handleViewActivated)
    }
    if (this._active && this.activationToken) {
      this.deactivate(this.activationToken)
    }
    this.viewRegistry = null
  }

  // ==================== 激活/反激活 ====================

  /**
   * 激活剖面工具，注册输入消费者。
   *
   * @param token - 激活令牌
   */
  activate(token: ActivationToken): void {
    this.validateToken(token)
    if (this._active) return
    this._active = true
    this.inputRouter.addConsumer(this.consumer)
  }

  /**
   * 反激活剖面工具，移除输入消费者并完成当前剖面。
   *
   * @param token - 激活令牌
   */
  deactivate(token: ActivationToken): void {
    this.validateToken(token)
    if (!this._active) return
    this._active = false
    this.inputRouter.removeConsumer(this.consumer)
    this.finishCurrentProfile()
    this.currentCamera = null
  }

  // ==================== 公开查询方法 ====================

  /**
   * 获取当前绘制中的路径点（只读），供 Overlay 插件读取中间状态。
   *
   * @returns 当前绘制的路径点数组，未绘制返回空数组
   */
  getCurrentPoints(): ReadonlyArray<ProfilePoint> {
    return this.currentPoints
  }

  /**
   * 获取所有已完成的剖面结果。
   *
   * @returns 剖面结果数组
   */
  getResults(): ReadonlyArray<ProfileResult> {
    return this.results
  }

  /**
   * 按 ID 移除指定剖面结果。
   *
   * @param id - 剖面结果 ID；不存在时忽略
   */
  removeResult(id: string): void {
    const prevLength = this.results.length
    this.results = this.results.filter((r) => r.id !== id)
    if (this.results.length !== prevLength) {
      this.emitResultChanged()
    }
  }

  /** 清空所有剖面结果 */
  clearResults(): void {
    if (this.results.length === 0) return
    this.results = []
    this.emitResultChanged()
  }

  /**
   * 修改已有剖面的宽度并重新采样截面点。
   *
   * @param id - 剖面结果 ID
   * @param width - 新宽度，单位米
   * @returns true 表示已更新；false 表示未找到对应结果
   */
  updateProfileWidth(id: string, width: number): boolean {
    const result = this.results.find((r) => r.id === id)
    if (!result) {
      return false
    }
    result.width = width
    // 仅在有相机时重新采样截面点，避免清空已有数据
    if (this.currentCamera) {
      const sectionPoints = this._sampleSectionPoints(result)
      if (sectionPoints.length > 0) {
        ;(result as { sectionPoints: ReadonlyArray<ProfileSectionPoint> }).sectionPoints =
          sectionPoints
      }
    }
    this.emitResultChanged()
    return true
  }

  /**
   * 从外部注入已有的剖面结果。
   *
   * @param result - 剖面结果
   */
  addResult(result: ProfileResult): void {
    this.results.push(result)
    this.emitResultChanged()
  }

  // ==================== 内部方法 ====================

  /** 处理指针按下事件 */
  private handlePointerDown(event: NormalizedPointerEvent): boolean {
    // 右键取消当前剖面
    if (event.button === 2) {
      this.cancelCurrentProfile()
      return true
    }

    // 仅处理左键
    if (event.button !== 0) return false

    if (!this.currentCamera) return false

    const pickResults = this.pickingService.pickAtScreen(event.screen, this.currentCamera)

    if (pickResults.length > 0) {
      const hitPoint = pickResults[0].point
      this.currentPoints.push({ position: hitPoint.clone() })
      this.emitResultChanged()
      return true
    }

    return false
  }

  /** 完成当前剖面，将点集转为剖面结果 */
  private finishCurrentProfile(): void {
    if (this.currentPoints.length >= 2 && this.currentCamera) {
      // 视口尺寸走 backend surface 快照，不读 legacy renderer。
      const surface = this.context.graphics.surface
      const width = computeDefaultWidth(
        this.currentPoints,
        this.currentCamera,
        surface.logicalWidth,
        surface.logicalHeight
      )
      const result = buildProfileResult(this.currentPoints, width)
      // 沿剖面路径采样点云截面点
      const sectionPoints = this._sampleSectionPoints(result)
      if (sectionPoints.length > 0) {
        ;(result as { sectionPoints: ReadonlyArray<ProfileSectionPoint> }).sectionPoints =
          sectionPoints
      }
      this.results.push(result)
    }
    this.currentPoints = []
    this.emitResultChanged()
  }

  /** 沿剖面路径做射线采样，收集截面点云数据 */
  private cancelCurrentProfile(): void {
    if (this.currentPoints.length === 0) {
      return
    }

    this.currentPoints = []
    this.emitResultChanged()
  }

  private _sampleSectionPoints(profile: ProfileResult): ProfileSectionPoint[] {
    if (!this.context.hasService('PickingService')) return []
    const picking = this.context.getService('PickingService')
    const camera = this.currentCamera
    if (!camera) return []

    const sectionPoints: ProfileSectionPoint[] = []
    const SAMPLE_STEP = 1 // 每米一个采样点
    const points = profile.points

    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i].position
      const end = points[i + 1].position
      const dir = new THREE.Vector3().subVectors(end, start)
      const segLen = dir.length()
      if (segLen === 0) continue
      dir.normalize()

      const steps = Math.max(1, Math.floor(segLen / SAMPLE_STEP))
      for (let s = 0; s <= steps; s++) {
        const t = s / steps
        const samplePos = new THREE.Vector3().lerpVectors(start, end, t)

        // 从采样点向下射线拾取
        const ray = new THREE.Ray(
          new THREE.Vector3(samplePos.x, samplePos.y, samplePos.z + 100),
          new THREE.Vector3(0, 0, -1)
        )
        const ndc = samplePos.clone().project(camera)
        const results = picking.pick({
          ndc: new THREE.Vector2(ndc.x, ndc.y),
          ray,
          camera,
          kinds: ['pointCloud'],
        })

        if (results.length > 0) {
          const hit = results[0]
          const attrs = hit.attributes
          sectionPoints.push({
            position: hit.point,
            classification: attrs?.classification,
            intensity: attrs?.intensity,
            rgba: attrs?.rgba as [number, number, number, number] | undefined,
          })
        }
      }
    }

    return sectionPoints
  }

  /** 通过事件总线通知剖面数据变化 */
  private emitResultChanged(): void {
    this.context.events.emit('tool.resultChanged', {
      name: this.name,
      data: this.results,
    })
  }

  /** 校验 token 合法性 */
  private validateToken(token: ActivationToken): void {
    if (token !== this.activationToken) {
      throw new Error('ProfilePlugin: 非法的 ActivationToken，操作被拒绝')
    }
  }
  private resolveActiveViewCamera(): THREE.Camera | null {
    return getActiveViewCamera(this.viewRegistry)
  }
}

export default ProfilePlugin
